import { useEffect, useMemo, useState } from "react";
import { AlertCircle, ChevronRight, MessageCircle, Sparkles } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { getMyEdgeReports, useQuery } from "wasp/client/operations";
import useTranslation from "../../../client/hooks/useTranslation";
import { Button } from "../../../client/components/ui/button";
import { edgyCreditsLabel, remainingEdgyCredits } from "../../../edgy/creditsLabel";
import { useLiteData } from "../../data/LiteDataProvider";
import { Card, cardCls, Empty, Stat, toneColor, type Tone } from "../../ui/ui";
import { EdgyLockCard } from "./EdgyLockCard";
import { HowItWorks } from "./HowItWorks";
import { ReportDetail } from "./ReportDetail";

const PAGE_SIZE = 12;

type Credits = {
  used: number;
  allowance: number;
  discoveryUsed: boolean;
  hasCredit: boolean;
  cooldownUntil: string | null;
  refillAt: string;
  killSwitch: boolean;
};

// Mêmes seuils que la liste Advanced (EdgyReportsPage.scoreTone).
function scoreTone(score: number): Tone {
  if (score > 70) return "gain";
  if (score >= 50) return "warn";
  return "loss";
}

/** Courbe de l'Edge Score : couleur selon la tendance (dernier vs premier), comme l'Advanced. */
function ScoreTrend({ scores }: { scores: number[] }) {
  const w = 240;
  const h = 44;
  const max = Math.max(...scores, 100);
  const min = Math.min(...scores, 0);
  const range = max - min || 1;
  const pts = scores.map((s, i) => [(i / (scores.length - 1)) * w, h - ((s - min) / range) * h] as const);
  const color = toneColor(scores[scores.length - 1] >= scores[0] ? "gain" : "loss");
  return (
    <svg viewBox={`-3 -3 ${w + 6} ${h + 6}`} className="w-full max-w-[240px] h-11 overflow-visible" aria-hidden>
      <polyline points={pts.map(p => p.join(",")).join(" ")} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      {pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.5" fill={color} />)}
    </svg>
  );
}

export function AnalysesTab({ credits, isLocked, refreshKey, onLaunch, onUpgrade }: {
  credits: Credits | null;
  isLocked: boolean;
  refreshKey: number;
  onLaunch: () => void;
  onUpgrade: () => void;
}) {
  const T = useTranslation();
  const t = T.lite.edgy;
  const th = T.edgy.history;
  const { fmt } = useLiteData();
  const [params, setParams] = useSearchParams();
  const openId = params.get("rapport");

  const [take, setTake] = useState(PAGE_SIZE);
  const [strategyFilter, setStrategyFilter] = useState("");
  const { data, isLoading, refetch } = useQuery(getMyEdgeReports, {
    skip: 0,
    take,
    ...(strategyFilter ? { playbookId: strategyFilter } : {}),
  });

  useEffect(() => {
    if (refreshKey > 0) void refetch();
  }, [refreshKey, refetch]);

  const setOpen = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("rapport", id); else next.delete("rapport");
    setParams(next);
  };

  // Regroupement par mois, plus récent d'abord (même logique que l'Advanced).
  const grouped = useMemo(() => {
    const groups = new Map<string, NonNullable<typeof data>["items"]>();
    for (const item of data?.items ?? []) {
      const d = new Date(item.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const list = groups.get(key);
      if (list) list.push(item); else groups.set(key, [item]);
    }
    return [...groups.entries()].map(([key, items]) => {
      const [y, m] = key.split("-");
      const label = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(fmt.locale, { month: "long", year: "numeric" });
      return { key, label, items };
    });
  }, [data, fmt.locale]);

  // Le filtre ne propose que les stratégies déjà analysées, comme l'Advanced.
  const strategies = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of data?.items ?? []) {
      if (item.playbookId && !seen.has(item.playbookId)) seen.set(item.playbookId, item.strategyName);
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [data]);

  if (openId) return <ReportDetail id={openId} onBack={() => setOpen(null)} />;

  const scores = data?.scoreHistory ?? [];
  const items = data?.items ?? [];
  const hasFilter = strategies.length > 1 || !!strategyFilter;

  return (
    <div className="space-y-5">
      {isLocked ? (
        <EdgyLockCard onUpgrade={onUpgrade} />
      ) : (
        <section className={`${cardCls} p-5 grid gap-5 grid-cols-2 lg:grid-cols-4 items-center`}>
          <Stat
            label={t.credits.title}
            value={credits ? remainingEdgyCredits(credits) : "-"}
            hint={credits ? t.credits.refill.replace("{date}", new Date(credits.refillAt).toLocaleDateString(fmt.locale)) : undefined}
          />
          <Stat label={t.stats.total} value={data ? data.total : "-"} />
          <Stat label={t.stats.last} value={scores.length ? scores[scores.length - 1] : "-"} />
          {scores.length >= 2 ? (
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">{th.scoreTrend}</div>
              <div className="font-mono tabular-nums text-sm font-semibold mt-1">{scores[0]} → {scores[scores.length - 1]}</div>
              <ScoreTrend scores={scores} />
            </div>
          ) : <div />}
          {/* Plus de crédit (ou analyse découverte) : même message que le pied du tiroir. */}
          {credits && remainingEdgyCredits(credits) === 0 && !credits.killSwitch && (
            <p className="col-span-2 lg:col-span-4 text-sm text-muted-foreground">{edgyCreditsLabel(credits, T.edgy.credits)}</p>
          )}
          {(credits?.killSwitch || credits?.cooldownUntil) && (
            <p className="col-span-2 lg:col-span-4 flex items-start gap-2 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              {credits.killSwitch
                ? T.edgy.errors.killSwitch
                : t.credits.cooldown.replace("{time}", new Date(credits.cooldownUntil as string).toLocaleTimeString(fmt.locale, { hour: "2-digit", minute: "2-digit" }))}
            </p>
          )}
        </section>
      )}

      {!isLocked && <HowItWorks />}

      <Card
        title={t.history.title}
        action={hasFilter ? (
          <label className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-muted-foreground shrink-0">{th.filterStrategy}</span>
            <select
              value={strategyFilter}
              onChange={e => { setStrategyFilter(e.target.value); setTake(PAGE_SIZE); }}
              className="h-8 max-w-[160px] sm:max-w-[220px] rounded-lg border border-border bg-card px-2 text-xs text-foreground"
            >
              <option value="">{th.allStrategies}</option>
              {strategies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        ) : undefined}
      >
        {isLoading ? (
          <Empty title={T.lite.common.loading} />
        ) : !items.length ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="w-11 h-11 rounded-2xl bg-primary/10 text-primary grid place-items-center"><Sparkles size={20} /></span>
            <div className="text-sm font-medium">{th.empty}</div>
            <p className="text-sm text-muted-foreground max-w-sm">{isLocked ? t.history.emptyLocked : t.history.emptyText}</p>
            {!isLocked && <Button size="sm" onClick={onLaunch}><Sparkles className="h-4 w-4 mr-1.5" />{t.newAnalysis}</Button>}
          </div>
        ) : (
          <div className="space-y-5">
            {grouped.map(group => (
              <div key={group.key} className="space-y-2">
                <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{group.label}</h3>
                {group.items.map(item => {
                  const tone = scoreTone(item.edgeScore);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setOpen(item.id)}
                      aria-label={`${t.history.open} ${item.strategyName}`}
                      className="w-full flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:border-primary/60 hover:bg-muted/50"
                    >
                      <span
                        className="w-11 h-11 shrink-0 rounded-xl grid place-items-center font-mono text-sm font-bold"
                        style={{ background: toneColor(tone, 0.12), color: toneColor(tone) }}
                      >
                        {item.edgeScore}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{item.strategyName}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {fmt.dateShort(item.createdAt)} · {th.tradesAnalysed.replace("{count}", String(item.tradesCount))}
                        </p>
                      </div>
                      {item.questionsCount > 0 && (
                        <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground" title={th.questionsCount.replace("{count}", String(item.questionsCount))}>
                          <MessageCircle className="h-3.5 w-3.5" />{item.questionsCount}
                        </span>
                      )}
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60" />
                    </button>
                  );
                })}
              </div>
            ))}
            {data && items.length < data.total && (
              <div className="flex justify-center">
                <Button variant="outline" size="sm" onClick={() => setTake(n => n + PAGE_SIZE)}>{th.loadMore}</Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
